<script>
import TitlePage from "../components/layout/TitlePage.vue";
import Card from "../components/ui/Card.vue";
import pagosService from "../services/pagos.service.js";

export default {
  components: {
    TitlePage,
    Card,
  },
  computed: {
    proveedorOptions() {
      return this.proveedores.map((proveedor) => ({
        id: proveedor.id,
        text: proveedor.nombre,
      }));
    },
  },
  data() {
    return {
      fetchIsPending: false,
      pagos: undefined,
      pageMeta: undefined,

      proveedores: [],
      tiposDocumentoContable: [],
      opcionesIsPending: false,
      opcionesDebounce: null,
      opcionesRequestId: 0,

      filters: {
        codigo_despacho: "",
        tipo_documento_contable: "",
        proveedor_id: "",
        fecha_pago: "",
      },

      fields: [
        { key: "despacho.codigo", label: "N° Despacho" },
        { key: "tipo.nombre", label: "Tipo" },
        { key: "tipo_documento_contable", label: "Documento Contable" },
        { key: "proveedor.nombre", label: "Proveedor" },
        { key: "monto", label: "Monto" },
        { key: "fecha_pago", label: "Fecha Documento" },
        { key: "usuario.name", label: "Usuario" },
        { key: "documentos", label: "Pagos" },
        { key: "acciones", label: "Acciones" },
      ],
    };
  },
  methods: {
    // Solo los filtros con valor, tal como los espera la API.
    filtrosAplicados() {
      return Object.entries(this.filters).reduce((params, [clave, valor]) => {
        if (valor) params[clave] = valor;
        return params;
      }, {});
    },
    async fetch() {
      this.fetchIsPending = true;
      try {
        const params = {
          page: this.pageMeta?.current_page || 1,
          ...this.filtrosAplicados(),
        };

        const { data, meta } = await pagosService.index(params);

        if (data && meta) {
          this.pagos = [...data];
          this.pageMeta = { ...meta };
        }
      } catch (e) {
        console.error(e);
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Hubo un error al obtener el listado de pagos.",
        });
      }
      this.fetchIsPending = false;
    },
    formatMonto(monto) {
      return new Intl.NumberFormat("es-CL", {
        currency: "CLP",
        style: "currency",
      }).format(monto ?? 0);
    },
    verDespacho(despachoId) {
      if (!despachoId) return;
      this.$router.push({
        name: "PerfilDespacho",
        params: { despachoId },
      });
    },
    downloadPago(id) {
      pagosService.downloadPago(id);
    },
    /**
     * Recarga las opciones de proveedor y documento contable según los filtros
     * activos, de modo que solo se ofrezca lo que realmente existe
     */
    async getOpciones() {
      const requestId = ++this.opcionesRequestId;
      this.opcionesIsPending = true;
      try {
        const { proveedores, tiposDocumentoContable } =
          await pagosService.getOpcionesFiltros(this.filtrosAplicados());

        // Ignorar respuestas de peticiones que ya quedaron obsoletas.
        if (requestId !== this.opcionesRequestId) return;

        this.proveedores = proveedores;
        this.tiposDocumentoContable = tiposDocumentoContable;
        this.descartarSeleccionesNoDisponibles();
      } catch (e) {
        console.error(e);
      } finally {
        if (requestId === this.opcionesRequestId) this.opcionesIsPending = false;
      }
    },
    /**
     * Limpia las selecciones que dejaron de existir en las nuevas opciones.
     */
    descartarSeleccionesNoDisponibles() {
      const proveedorSigueDisponible = this.proveedores.some(
        (proveedor) => String(proveedor.id) === String(this.filters.proveedor_id)
      );
      if (this.filters.proveedor_id && !proveedorSigueDisponible) {
        this.filters.proveedor_id = "";
      }

      if (
        this.filters.tipo_documento_contable &&
        !this.tiposDocumentoContable.includes(
          this.filters.tipo_documento_contable
        )
      ) {
        this.filters.tipo_documento_contable = "";
      }
    },
    aplicarFiltros() {
      // Volver a la primera página; el watcher recarga si ya no estamos en ella.
      if (this.pageMeta && this.pageMeta.current_page !== 1) {
        this.pageMeta.current_page = 1;
      } else {
        this.fetch();
      }
    },
    limpiarFiltros() {
      this.filters = {
        codigo_despacho: "",
        tipo_documento_contable: "",
        proveedor_id: "",
        fecha_pago: "",
      };
      this.aplicarFiltros();
    },
  },
  watch: {
    "pageMeta.current_page": function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetch();
      }
    },
    filters: {
      deep: true,
      handler() {
        // Se agrupan los cambios seguidos (ej. tipeo del código de despacho).
        clearTimeout(this.opcionesDebounce);
        this.opcionesDebounce = setTimeout(() => this.getOpciones(), 400);
      },
    },
  },
  mounted() {
    this.fetch();
    this.getOpciones();
  },
  beforeDestroy() {
    clearTimeout(this.opcionesDebounce);
  },
};
</script>

<template>
  <div class="flex flex-col gap-8">
    <title-page title="Pagos" />

    <card title="Filtros" :boton="false">
      <form
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        @submit.prevent="aplicarFiltros"
      >
        <div class="flex flex-col text-left">
          <label>Código de despacho</label>
          <input type="text" v-model="filters.codigo_despacho" placeholder="Filtrar por despacho" />
        </div>

        <div class="flex flex-col text-left">
          <label>Documento contable</label>
          <select
            v-model="filters.tipo_documento_contable"
            :disabled="opcionesIsPending"
          >
            <option value="">Todos</option>
            <option
              v-for="(tipo, index) in tiposDocumentoContable"
              :key="index"
              :value="tipo"
            >
              {{ tipo }}
            </option>
          </select>
        </div>

        <div class="flex flex-col text-left">
          <label>Proveedor</label>
          <Select2
            v-model="filters.proveedor_id"
            :options="proveedorOptions"
            :settings="{
              placeholder: 'Seleccione un proveedor',
              width: '100%',
              allowClear: true,
              disabled: opcionesIsPending,
            }"
          />
        </div>

        <div class="flex flex-col text-left">
          <label>Fecha de documento</label>
          <input type="date" v-model="filters.fecha_pago" />
        </div>

        <div class="flex flex-row justify-end items-center gap-2 lg:col-span-4">
          <button type="submit" class="btn-principal">Filtrar</button>
          <button
            type="button"
            class="btn-secundario btn-small"
            @click="limpiarFiltros"
          >
            Limpiar
          </button>
        </div>
      </form>
    </card>

    <div class="flex flex-col gap-4 bg-white shadow-md p-4">
      <div class="flex flex-row items-center justify-between gap-2">
        <h1 class="!text-xl !font-bold">Pagos</h1>
      </div>

      <div
        v-if="fetchIsPending"
        class="text-center mx-auto my-auto h-full text-[#ffce1e]"
      >
        <b-spinner class="align-middle"></b-spinner>
        <strong> Cargando...</strong>
      </div>

      <div v-else>
        <b-table
          class="text-left"
          hover
          show-empty
          responsive
          :items="pagos"
          :fields="fields"
        >
          <template #empty>
            <h4 class="text-center">No hay elementos en la lista</h4>
          </template>

          <template #cell(codigo)="row">
            {{ row.item?.despacho?.codigo ?? "-" }}
          </template>

          <template #cell(cliente)="row">
            {{ row.item?.cliente?.nombre ?? "-" }}
          </template>

          <template #cell(tipo.nombre)="row">
            {{ row.item?.tipo_pago?.nombre ?? "-" }}
          </template>

          <template #cell(proveedor)="row">
            {{ row.item?.proveedor?.nombre ?? "-" }}
          </template>

          <template #cell(monto)="row">
            {{ formatMonto(row.item?.monto) }}
          </template>
          <template #cell(documentos)="row">
        <button
          class="btn-archivo-tabla"
          :title="pago.ruta"
          v-for="(pago, index) in row.item.archivos"
          :key="index"
          style="width: 20px"
          @click="downloadPago(pago.id)"
        >
          <inline-svg
            v-if="pago.ruta.split('.').at(-1) == 'pdf'"
            src="../../assets/images/archivo-pdf.svg"
          />
          <inline-svg
            v-if="
              pago.ruta.split('.').at(-1) == 'png' ||
              pago.ruta.split('.').at(-1) == 'jpg' ||
              pago.ruta.split('.').at(-1) == 'jpeg'
            "
            src="../../assets/images/archivo-png.svg"
          />
        </button>
      </template>

          <template #cell(acciones)="row">
            <div class="flex flex-row items-center justify-center gap-2 px-2">
              <button
                class="btn-extra-primario"
                @click="
                  verDespacho(row.item?.despacho_id ?? row.item?.despacho?.id)
                "
              >
                <fas :icon="['fas', 'eye']"></fas>
                Ver despacho
              </button>
            </div>
          </template>
        </b-table>

        <b-pagination
          v-if="pageMeta"
          v-model="pageMeta.current_page"
          style="width: 100px; margin: auto"
          :total-rows="pageMeta.total"
          :per-page="pageMeta.per_page"
          align="fill"
          size="sm"
          class="my-0"
        ></b-pagination>
      </div>
    </div>
  </div>
</template>
